/**
 * <popular-brands-tabs>
 * Handles two independent things, both scoped to this single component
 * instance (there's only one per section, and each showcase's nav buttons
 * are looked up per-showcase via querySelectorAll, not a single global
 * querySelector -- avoiding the "only the first pair gets wired up" bug
 * class entirely, since there's no ambiguity about which buttons belong to
 * which showcase here):
 *
 * 1. Tab clicks: toggle which .pbr-showcase is visible (all are rendered
 *    server-side for SEO; only the active one is shown via a CSS class).
 * 2. Each showcase's own prev/next buttons: scroll its own product row by
 *    roughly two cards at a time, exactly like the reference prototype.
 */
class PopularBrandsTabs extends HTMLElement {
    connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[data-pbr-tab]'));
        this.showcases = Array.from(this.querySelectorAll('.pbr-showcase'));

        this.tabs.forEach((tab) => {
            tab.addEventListener('click', () => this.activate(tab));
        });

        this.showcases.forEach((showcase) => this.initProductRow(showcase));
    }

    activate(tab) {
        const targetId = tab.dataset.target;

        this.tabs.forEach((t) => {
            const isTarget = t === tab;
            t.classList.toggle('is-active', isTarget);
            t.setAttribute('aria-selected', isTarget ? 'true' : 'false');
        });

        this.showcases.forEach((showcase) => {
            showcase.classList.toggle('is-active', showcase.id === targetId);
        });
    }

    initProductRow(showcase) {
        const track = showcase.querySelector('[data-pbr-track]');
        const prevBtn = showcase.querySelector('.pbr-nav-btn.prev');
        const nextBtn = showcase.querySelector('.pbr-nav-btn.next');
        if (!track) return;

        const scrollByCards = (dir) => {
            const card = track.querySelector('.pbr-card');
            const cardWidth = card ? card.getBoundingClientRect().width : 240;
            track.scrollBy({ left: dir * (cardWidth + 12) * 2, behavior: 'smooth' });
        };

        if (prevBtn) prevBtn.addEventListener('click', () => scrollByCards(-1));
        if (nextBtn) nextBtn.addEventListener('click', () => scrollByCards(1));

        const updateDisabled = () => {
            if (!prevBtn && !nextBtn) return;
            const maxScroll = track.scrollWidth - track.clientWidth;
            if (prevBtn) prevBtn.classList.toggle('disabled', track.scrollLeft <= 1);
            if (nextBtn) nextBtn.classList.toggle('disabled', track.scrollLeft >= maxScroll - 1);
        };

        track.addEventListener('scroll', updateDisabled, { passive: true });
        if (window.ResizeObserver) {
            new ResizeObserver(updateDisabled).observe(track);
        }
        updateDisabled();
    }
}

if (!customElements.get('popular-brands-tabs')) {
    customElements.define('popular-brands-tabs', PopularBrandsTabs);
}